import {
  useHttpClient,
  useNetworkStatus,
  useReducerStorage as useReducer,
  useToast,
} from '@airship-platform/airship-patterns';
import { ProposalTemplateTag } from '@airship-platform/airship-proposal';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {
  ProposalTemplate,
  ProposalTemplateOption,
  QueryProposalTemplatesOptionsResponse,
  QueryProposalTemplatesResponse,
} from './proposalTemplate.models';
import {
  initState,
  proposalTemplatesReducer,
  ProposalTemplateState,
} from './proposalTemplate.reducer';

const mapOptions = (
  options: QueryProposalTemplatesResponse['proposals'][number]['options'],
  pricebookItems: Record<
    string,
    QueryProposalTemplatesResponse['pricebookItems'][string]
  >
) =>
  options.map((option) => ({
    ...option,
    products: option.products.flatMap((prod) => {
      const pricebookItem = pricebookItems[prod.pricebookId];
      return pricebookItem
        ? [
            {
              ...prod,
              ...pricebookItem,
              description: prod.description || pricebookItem.description,
              defaultPriceType:
                prod.defaultPriceType || pricebookItem.defaultPriceType,
            },
          ]
        : [];
    }),
  }));

interface ProposalTemplatesContextProps {
  proposalTemplateOptions: ProposalTemplateOption[];
  proposalTemplates: ProposalTemplate[];
  tags: ProposalTemplateTag[];
  isLoadedOptions: boolean;
  isLoadingOptions: boolean;
  isLoadedTemplates: boolean;
  isLoadingTemplates: boolean;
  isLoadedTags: boolean;
  isLoadingTags: boolean;
  error: string | null;
  queryProposalTemplates: (
    params: { businessUnitIds: string[] },
    config?: { signal?: AbortSignal }
  ) => Promise<void>;
  queryProposalTemplateOptions: (
    params: { businessUnitIds: string[] },
    config?: { signal?: AbortSignal }
  ) => Promise<void>;
  fetchProposalTemplateTags: (config?: {
    signal?: AbortSignal;
  }) => Promise<void>;
}

const ProposalTemplatesContext = createContext<
  ProposalTemplatesContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<ProposalTemplateState>;
}>;

export const ProposalTemplatesProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(
    proposalTemplatesReducer,
    {
      ...initState,
      ...initialState,
    },
    {
      storage: sessionStorage,
      ttl: 10 * 60 * 1000, // 10 minutes
      key: 'airship-cache:proposal-templates',
      select: (s) => ({
        proposalTemplateIds: s.proposalTemplateIds,
        proposalTemplateEntities: s.proposalTemplateEntities,
        proposalTemplateOptionIds: s.proposalTemplateOptionIds,
        proposalTemplateOptionEntities: s.proposalTemplateOptionEntities,
        tags: s.tags,
      }),
    }
  );

  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    state.error && showToast(state.error, 'error');
  }, [state.error, showToast]);

  const proposalTemplateOptions = useMemo(
    () =>
      state.proposalTemplateOptionIds.map(
        (id) => state.proposalTemplateOptionEntities[id]
      ),
    [state.proposalTemplateOptionEntities, state.proposalTemplateOptionIds]
  );

  const proposalTemplates = useMemo(
    () =>
      state.proposalTemplateIds.map((id) => state.proposalTemplateEntities[id]),
    [state.proposalTemplateEntities, state.proposalTemplateIds]
  );

  const queryProposalTemplates: ProposalTemplatesContextProps['queryProposalTemplates'] =
    useCallback(
      async (params, config) => {
        if (!isOnline()) {
          dispatch({
            type: 'SET_LOADING_STATE',
            payload: { isLoadingTemplates: false, isLoadedTemplates: true },
          });
          return;
        }

        dispatch({ type: 'QUERY_PROPOSAL_TEMPLATES' });
        try {
          const res = await httpClient.get<QueryProposalTemplatesResponse>(
            `/proposal-templates/query-proposals`,
            {
              signal: config?.signal,
              params,
            }
          );

          dispatch({
            type: 'QUERY_PROPOSAL_TEMPLATES_SUCCESS',
            payload: {
              proposalTemplates: res.proposals.map((template) => ({
                ...template,
                options: mapOptions(template.options, res.pricebookItems),
              })),
            },
          });
        } catch (error) {
          if (error instanceof Error && error.message !== 'canceled') {
            dispatch({
              type: 'QUERY_PROPOSAL_TEMPLATES_FAILURE',
              payload: { error: `${error}` },
            });
          }
        }
      },
      [httpClient, isOnline]
    );

  const queryProposalTemplateOptions: ProposalTemplatesContextProps['queryProposalTemplateOptions'] =
    useCallback(
      async (params, config) => {
        if (!isOnline()) {
          dispatch({
            type: 'SET_LOADING_STATE',
            payload: { isLoadingOptions: false, isLoadedOptions: true },
          });
          return;
        }

        dispatch({ type: 'QUERY_PROPOSAL_TEMPLATE_OPTIONS' });
        try {
          const res =
            await httpClient.get<QueryProposalTemplatesOptionsResponse>(
              `/proposal-templates/query-options`,
              {
                signal: config?.signal,
                params,
              }
            );

          dispatch({
            type: 'QUERY_PROPOSAL_TEMPLATE_OPTIONS_SUCCESS',
            payload: {
              proposalTemplateOptions: mapOptions(
                res.options,
                res.pricebookItems
              ),
            },
          });
        } catch (error) {
          if (error instanceof Error && error.message !== 'canceled') {
            dispatch({
              type: 'QUERY_PROPOSAL_TEMPLATE_OPTIONS_FAILURE',
              payload: { error: `${error}` },
            });
          }
        }
      },
      [httpClient, isOnline]
    );

  const fetchProposalTemplateTags: ProposalTemplatesContextProps['fetchProposalTemplateTags'] =
    useCallback(
      async (config) => {
        if (!isOnline()) {
          dispatch({
            type: 'SET_LOADING_STATE',
            payload: { isLoadingTags: false, isLoadedTags: true },
          });
          return;
        }

        dispatch({ type: 'FETCH_PROPOSAL_TEMPLATE_TAGS' });
        try {
          const tags = await httpClient.get<ProposalTemplateTag[]>(
            `/proposal-templates/tags`,
            { signal: config?.signal }
          );
          dispatch({
            type: 'FETCH_PROPOSAL_TEMPLATE_TAGS_SUCCESS',
            payload: { tags },
          });
        } catch (error) {
          if (error instanceof Error && error.message !== 'canceled') {
            dispatch({
              type: 'FETCH_PROPOSAL_TEMPLATE_TAGS_FAILURE',
              payload: { error: `${error}` },
            });
          }
        }
      },
      [httpClient, isOnline]
    );

  return (
    <ProposalTemplatesContext.Provider
      value={{
        proposalTemplateOptions,
        proposalTemplates,
        tags: state.tags,
        isLoadingTemplates: state.isLoadingTemplates,
        isLoadingOptions: state.isLoadingOptions,
        isLoadedTemplates: state.isLoadedTemplates,
        isLoadedTags: state.isLoadedTags,
        isLoadingTags: state.isLoadingTags,
        isLoadedOptions: state.isLoadedOptions,
        error: state.error,
        queryProposalTemplates,
        queryProposalTemplateOptions,
        fetchProposalTemplateTags,
      }}
    >
      {children}
    </ProposalTemplatesContext.Provider>
  );
};

export const useProposalTemplates = () => {
  const context = useContext(ProposalTemplatesContext);
  if (context === undefined) {
    throw new Error(
      'useProposalTemplates must be used within a ProposalTemplatesProvider'
    );
  }
  return context;
};
